// Based on https://github.com/floydjones1/react-lazy-retry/blob/main/src/index.ts

/* oxlint-disable @typescript-eslint/no-explicit-any */
import {ComponentType, lazy} from 'react';

type ComponentPromise<T = any> = Promise<{default: ComponentType<T>}>;

const MAX_RETRIES = 5;
const RETRY_INTERVAL = 1000;

// A wrapper for React.lazy which retries the import for a limited number of times on chunk load error.
export default function lazyRetry(
  component: () => ComponentPromise, // A function supported by React.lazy (expected to be a dynamic import)
  retries?: number,
  interval?: number,
): React.LazyExoticComponent<React.ComponentType<any>> {
  return lazy(() => retry(component, retries, interval));
}

// Execute a Promise-returning function after a set delay, with an iteration countdown
function retry(
  fn: () => ComponentPromise, // A Promise-returning function
  retriesLeft = MAX_RETRIES, // The maximum number of times to retry
  interval = RETRY_INTERVAL, // The base amount of time to wait between retries
): ComponentPromise {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(error => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error);
            return;
          }
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}
